import { idbGet, idbSet } from "@/lib/idb";

// Bump when the bundled JSON files change so all users get the fresh
// normalized data on their next visit (invalidates stale IDB entries).
export const BIBLE_DATA_VERSION = 1;
const IDB_KEY = (lang) => `bible:${lang}`;

// CPDV / SpaRV-style: already in the canonical shape.
function normalizeStandard(raw) {
    return {
        translation: raw.translation || "",
        books: (raw.books || []).map((b) => ({
            name: (b.name || "").trim(),
            chapters: (b.chapters || []).map((ch) => ({
                chapter: typeof ch.chapter === "string" ? parseInt(ch.chapter, 10) : ch.chapter,
                verses: (ch.verses || []).map((v) => ({
                    verse: typeof v.verse === "string" ? parseInt(v.verse, 10) : v.verse,
                    text: (v.text || "").trim(),
                })),
            })),
        })),
    };
}

// "Biblia de Jerusalén" / bibliacatolica.com.br shape:
//   { "Génesis": { abreviacion, testamento, chapters: [{ chapter:"1", verses: { "1": "..." }}] }, ... }
function normalizeJerusalenES(raw) {
    const books = [];
    for (const [rawName, info] of Object.entries(raw)) {
        if (!info || typeof info !== "object" || !Array.isArray(info.chapters)) continue;
        const chapters = info.chapters.map((ch) => {
            const versesObj = ch.verses || {};
            const verses = Object.entries(versesObj)
                .map(([num, text]) => ({
                    verse: parseInt(num, 10),
                    text: (text || "").trim(),
                }))
                .filter((v) => Number.isFinite(v.verse))
                .sort((a, b) => a.verse - b.verse);
            return {
                chapter: typeof ch.chapter === "string" ? parseInt(ch.chapter, 10) : ch.chapter,
                verses,
            };
        });
        books.push({ name: rawName.trim(), chapters });
    }
    return { translation: "La Biblia de Jerusalén", books };
}

// Per-language data files. Both ultimately get normalized to:
//   { translation: string, books: [{ name, chapters: [{ chapter, verses: [{ verse, text }] }] }] }
const SOURCES = {
    en: { url: "/data/bible-en.json", normalize: normalizeStandard },
    es: { url: "/data/bible-es.json", normalize: normalizeJerusalenES },
};

// Module-level caches keyed by lang. The 5–10 MB JSONs are fetched +
// indexed only once per session and survive route changes.
const _cache = {};      // lang → parsed data
const _inflight = {};   // lang → Promise
const _flatIndex = {};  // lang → flat verse index for search

export function getCachedBible(lang) {
    return _cache[lang] || null;
}

export async function loadBible(lang) {
    if (_cache[lang]) return _cache[lang];
    if (_inflight[lang]) return _inflight[lang];
    const src = SOURCES[lang];
    if (!src) throw new Error(`No bible source for lang ${lang}`);

    // 1) Try IDB first — instant hydration with no JSON.parse + no normalize.
    //    If the stored version doesn't match BIBLE_DATA_VERSION we fall through
    //    to the network fetch and overwrite the entry.
    _inflight[lang] = (async () => {
        try {
            const cached = await idbGet(IDB_KEY(lang));
            if (cached && cached.version === BIBLE_DATA_VERSION && cached.payload) {
                _cache[lang] = cached.payload;
                return cached.payload;
            }
        } catch { /* fall through to fetch */ }

        // 2) Network fetch → normalize → memoize in-memory + IDB.
        const res = await fetch(src.url, { cache: "force-cache" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const raw = await res.json();
        const data = src.normalize(raw);
        _cache[lang] = data;
        // Store asynchronously — don't block UI on the IDB write.
        idbSet(IDB_KEY(lang), BIBLE_DATA_VERSION, data).catch(() => {});
        return data;
    })().finally(() => { delete _inflight[lang]; });
    return _inflight[lang];
}

export function getFlatIndex(lang, data) {
    if (_flatIndex[lang]) return _flatIndex[lang];
    const out = [];
    for (const b of data.books) {
        for (const ch of b.chapters) {
            for (const v of ch.verses) {
                out.push({
                    book: b.name,
                    chapter: ch.chapter,
                    verse: v.verse,
                    text: (v.text || "").trim(),
                });
            }
        }
    }
    _flatIndex[lang] = out;
    return out;
}

// Display-name resolver. Both files now ship localized names directly,
// so this is a passthrough — kept for symmetry and future overrides.
export function displayName(rawName /* , lang */) {
    return rawName;
}

// Parse "John 3:16" / "Juan 3:16" / "1 Reyes 17:5" / "Genesis 50".
// Chapter number is required so this never matches arbitrary text the
// user is mid-typing.
export function parseReference(query, books) {
    const q = query.trim();
    if (!q) return null;
    const m = q.match(/^(.+?)\s+(\d+)(?::(\d+))?$/);
    if (!m) return null;
    const target = m[1].trim().toLowerCase();
    const chap = parseInt(m[2], 10);
    const verse = m[3] ? parseInt(m[3], 10) : null;

    const found = books.find((b) => b.name.toLowerCase() === target)
        || books.find((b) => b.name.toLowerCase().startsWith(target))
        || books.find((b) => b.name.toLowerCase().includes(target));

    if (!found) return null;
    return { book: found, chapter: chap, verse };
}
